import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Home from './game/components/Home';
import Lobby from './game/components/Lobby';
import { GameTable } from './game/components';
import ConfirmDialog from './game/components/ConfirmDialog';
import ConnectionStatus from './game/components/ConnectionStatus';
import VoiceChat from './game/components/VoiceChat';
import { socket } from './socket';
import './App.css';

type Screen = 'home' | 'lobby' | 'game';

function App() {
  const [screen, setScreen] = useState<Screen>('home');
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const screenRef = useRef<Screen>('home');

  const initialRoomCode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('room') || '';
  }, []);

  const navigateTo = useCallback((target: Screen) => {
    setScreen(target);
    screenRef.current = target;
    window.history.pushState({ screen: target }, '', window.location.pathname);
  }, []);

  const goHome = useCallback(() => {
    socket.emit('leave_room');
    setScreen('home');
    screenRef.current = 'home';
    setRoomCode('');
    setPlayerName('');
    setIsHost(false);
    setShowBackConfirm(false);
    window.history.replaceState({ screen: 'home' }, '', window.location.pathname);
  }, []);

  // Browser back/forward and mobile swipe-back
  useEffect(() => {
    window.history.replaceState({ screen: 'home' }, '', window.location.pathname);

    const onPopState = () => {
      const current = screenRef.current;
      if (current === 'lobby') {
        goHome();
      } else if (current === 'game') {
        window.history.pushState({ screen: 'game' }, '', window.location.pathname);
        setShowBackConfirm(true);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [goHome]);

  const handleRoomCreated = (code: string, name: string) => {
    setRoomCode(code);
    setPlayerName(name);
    setIsHost(true);
    navigateTo('lobby');
  };

  const handleRoomJoined = (code: string, name: string) => {
    setRoomCode(code);
    setPlayerName(name);
    setIsHost(false);
    navigateTo('lobby');
  };

  const handleRejoinGame = (code: string, name: string) => {
    setRoomCode(code);
    setPlayerName(name);
    setIsHost(false);
    navigateTo('game');
  };

  const handleGameStarted = () => {
    navigateTo('game');
  };

  const handleLeave = () => {
    goHome();
  };

  const voiceChat = screen !== 'home' && roomCode && playerName
    ? <VoiceChat roomCode={roomCode} playerName={playerName} screen={screen} />
    : null;

  let content: React.ReactNode = null;

  switch (screen) {
    case 'home':
      content = (
        <Home
          onRoomCreated={handleRoomCreated}
          onRoomJoined={handleRoomJoined}
          onRejoinGame={handleRejoinGame}
          initialRoomCode={initialRoomCode}
        />
      );
      break;
    case 'lobby':
      content = (
        <Lobby
          roomCode={roomCode}
          isHost={isHost}
          playerName={playerName}
          onGameStarted={handleGameStarted}
          onLeave={handleLeave}
        />
      );
      break;
    case 'game':
      content = (
        <>
          <GameTable
            roomCode={roomCode}
            playerName={playerName}
            onLeave={handleLeave}
          />
          <ConfirmDialog
            open={showBackConfirm}
            title="Leave Game?"
            message="Your spot will be taken over by a computer player. Are you sure you want to leave?"
            confirmText="Leave"
            cancelText="Stay"
            variant="danger"
            onConfirm={goHome}
            onCancel={() => setShowBackConfirm(false)}
          />
        </>
      );
      break;
  }

  return (
    <div className="App">
      <ConnectionStatus />
      {content}
      {voiceChat}
    </div>
  );
}

export default App;
